import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { LanguageService } from './language.service';
import { AuthService } from './auth.service';

export interface ResolvedExampleItem { label: string; example: string; }
export interface ResolvedNicheUiConfig {
  welcome_title: string;
  welcome_subtitle: string;
  placeholders: string[];
  examples: ResolvedExampleItem[];
  onboarding_features: string[];
}
export interface ResolvedNiche {
  id: string; name: string; icon: string; description: string;
  is_default: boolean; ui_config: ResolvedNicheUiConfig;
}

@Injectable({ providedIn: 'root' })
export class NicheService {
  private http = inject(HttpClient);
  private languageService = inject(LanguageService);
  private authService = inject(AuthService);

  private rawNiches = signal<any[]>([]);
  private currentNicheId = signal<string | null>(null);

  // ── Loading state for niche update ──
  private _updatingNicheId = signal<string | null>(null);
  /** The ID of the niche currently being saved to the backend (null if idle). */
  readonly updatingNicheId = this._updatingNicheId.asReadonly();

  // THE MAGIC: These computed signals automatically re-run when currentLanguage() changes.
  readonly availableNiches = computed<ResolvedNiche[]>(() => {
    const lang = this.languageService.currentLanguage(); 
    return this.rawNiches().map(n => this.resolveNiche(n, lang));
  });

  readonly currentNiche = computed<ResolvedNiche | null>(() => {
    const lang = this.languageService.currentLanguage();
    const id = this.currentNicheId();
    const raw = this.rawNiches().find(n => n.id === id);
    return raw ? this.resolveNiche(raw, lang) : null;
  });

  // ── High‑stakes niche detection ──
  private highStakesNames = new Set([
    'arbitrage',
    'luxury-flipping',
    'travel-hacking',
    'ticket-reselling',
    'digital-assets'
  ]);

  readonly isHighStakesNiche = computed<boolean>(() => {
    const niche = this.currentNiche();
    return niche ? this.highStakesNames.has(niche.name.toLowerCase()) : false;
  });

  async loadNiches(): Promise<void> {
    try {
      const resp: any = await lastValueFrom(this.http.get('/niches'));
      if (resp.success && Array.isArray(resp.niches)) {
        // Standardize on ui_config, fallback to ui just in case
        const mapped = resp.niches.map((n: any) => ({ ...n, ui_config: n.ui_config || n.ui || {} }));
        this.rawNiches.set(mapped);
        // No automatic side‑effect: the caller is responsible for setting the
        // active niche via setCurrentNiche() or updateUserNiche().
      }
    } catch (err) { console.error('Failed to load niches', err); }
  }

  /** Sets the current niche by id (no backend call). */
  setCurrentNiche(nicheId: string) {
    this.currentNicheId.set(nicheId);
  }

  /**
   * Optimistically updates the current niche and persists the choice to the backend.
   * Returns a promise that resolves when the backend request finishes (success or error).
   */
  async updateUserNiche(nicheId: string): Promise<void> {
    // Optimistic UI first
    this.currentNicheId.set(nicheId);
    this._updatingNicheId.set(nicheId);

    try {
      await lastValueFrom(this.http.patch('/users/me/niche', { niche_id: nicheId }));
      // After a successful backend update, synchronize the persistent
      // session state through the dedicated auth‑service method.
      this.authService.updatePersistedNicheId(nicheId);
    } catch (err) {
      console.error('Failed to update user niche', err);
    } finally {
      this._updatingNicheId.set(null);
    }
  }

  // --- Pure Resolution Helpers ---
  private resolveNiche(n: any, lang: string): ResolvedNiche {
    const ui = n.ui_config || {};
    return {
      id: n.id, icon: n.icon, is_default: n.is_default,
      name: this.resolveStr(n.name, lang),
      description: this.resolveStr(n.description, lang),
      ui_config: {
        welcome_title: this.resolveStr(ui.welcome_title, lang),
        welcome_subtitle: this.resolveStr(ui.welcome_subtitle, lang),
        placeholders: this.resolveArr(ui.placeholders, lang),
        onboarding_features: this.resolveArr(ui.onboarding_features, lang),
        examples: (ui.examples || []).map((ex: any) => ({
          label: this.resolveStr(ex.label, lang),
          example: this.resolveStr(ex.example, lang)
        }))
      }
    };
  }

  private resolveStr(obj: any, lang: string): string {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return obj[lang] || obj['en'] || Object.values(obj)[0] || '';
  }

  private resolveArr(obj: any, lang: string): string[] {
    if (!obj) return [];
    if (Array.isArray(obj)) return obj;
    return obj[lang] || obj['en'] || Object.values(obj)[0] || [];
  }
}
